import React from "react";
import type { Omittable } from "@salestrak/ui";

// Lazy: the runs dialog is an automations-admin surface opened from issue
// menus, so its chunk loads on first open instead of shipping in the
// always-loaded workspace layout bundle.
const AutomationRunsDialog = React.lazy(() =>
  import("#app/components/automations/runs/automation-runs-dialog").then((m) => ({
    default: m.AutomationRunsDialog,
  })),
);

type AutomationRunsDialogPayload = Omittable<{
  issueId: string;
  issueName?: string | null;
}>;

type AutomationRunsDialogActions = {
  openDialog: (payload: AutomationRunsDialogPayload) => void;
  closeDialog: () => void;
};

// Same emitter trick as item-share-dialog-context — callers that live inside
// bottom-sheet portals (kanban card menus on mobile, the issue-view three-dot
// menu, etc.) can't read React context, so we route open/close through a
// module-scoped Set instead.
type Listener = (payload: AutomationRunsDialogPayload | null) => void;
const listeners = new Set<Listener>();
function emit(payload: AutomationRunsDialogPayload | null) {
  listeners.forEach((l) => l(payload));
}

/** Get stable open/close callbacks. Works inside bottom-sheet portals. */
export function useAutomationRunsDialogActions(): AutomationRunsDialogActions {
  return React.useMemo<AutomationRunsDialogActions>(
    () => ({
      openDialog: (payload) => emit(payload),
      closeDialog: () => emit(null),
    }),
    [],
  );
}

export function AutomationRunsDialogMount() {
  const [open, setOpen] = React.useState(false);
  // Latched on first open: until then nothing is mounted at all, so the lazy
  // chunk costs nothing at app load. Kept mounted after so the close
  // animation can play.
  const [mounted, setMounted] = React.useState(false);
  // The issue that opened the dialog; kept on close so the closing animation
  // doesn't flash an empty title.
  const [issue, setIssue] = React.useState<AutomationRunsDialogPayload | null>(
    null,
  );

  React.useEffect(() => {
    const listener: Listener = (payload) => {
      if (payload) {
        setMounted(true);
        setIssue(payload);
        setOpen(true);
      } else {
        setOpen(false);
      }
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  if (!mounted) return null;

  return (
    <React.Suspense fallback={null}>
      <AutomationRunsDialog
        open={open}
        onClose={() => setOpen(false)}
        issueId={issue?.issueId ?? null}
        issueName={issue?.issueName}
      />
    </React.Suspense>
  );
}
